import { useEffect, useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";

export default function DraftEditDialog({ draft, open, onOpenChange, onSaved }) {
  const [caption, setCaption] = useState("");
  const [hashtags, setHashtags] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newImagePreview, setNewImagePreview] = useState(null);
  const [newImageFile, setNewImageFile] = useState(null);
  const [regenerateVideo, setRegenerateVideo] = useState(true);
  const fileRef = useRef(null);

  useEffect(() => {
    if (draft) {
      setCaption(draft.caption || "");
      setHashtags((draft.hashtags || []).join(" "));
      setScheduledAt(
        draft.scheduled_at
          ? new Date(draft.scheduled_at).toISOString().slice(0, 16)
          : ""
      );
      setNewImagePreview(null);
      setNewImageFile(null);
    }
  }, [draft]);

  const pickImage = (f) => {
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) {
      toast.error("Image too large (max 20MB)");
      return;
    }
    setNewImageFile(f);
    const r = new FileReader();
    r.onload = (e) => setNewImagePreview(e.target.result);
    r.readAsDataURL(f);
  };

  const uploadNewImage = async () => {
    if (!newImageFile) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", newImageFile);
      fd.append("regenerate_video", String(regenerateVideo));
      await api.post(`/drafts/${draft.draft_id}/image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Image replaced" + (regenerateVideo ? " + new video rendering." : "."));
      setNewImageFile(null);
      setNewImagePreview(null);
      onSaved?.();
    } catch {
      toast.error("Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/drafts/${draft.draft_id}`, {
        caption,
        hashtags: hashtags.split(/\s+/).filter(Boolean),
        scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
      });
      toast.success("Draft updated");
      onOpenChange(false);
      onSaved?.();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const currentImageUrl = draft?.image_url
    ? (draft.image_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${draft.image_url}` : draft.image_url)
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0A0A0A] border-white/10 rounded-sm max-w-2xl text-white max-h-[92vh] overflow-y-auto"
        data-testid="draft-edit-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">
            Edit draft
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-2">
          {/* Replace image section */}
          <div>
            <div className="label-mono mb-2">Artwork</div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Current</div>
                {currentImageUrl ? (
                  <img src={currentImageUrl} alt="Current artwork" className="w-full aspect-square object-cover border border-white/10" />
                ) : (
                  <div className="w-full aspect-square border border-white/10 flex items-center justify-center text-white/30 label-mono">
                    No image
                  </div>
                )}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Replace with</div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={(e) => pickImage(e.target.files?.[0])}
                  data-testid="edit-replace-file-input"
                />
                {newImagePreview ? (
                  <div className="relative group">
                    <img src={newImagePreview} alt="Replacement preview" className="w-full aspect-square object-cover border border-[#FF3B00]/50" />
                    <button
                      type="button"
                      className="absolute top-2 right-2 bg-black/80 border border-white/20 p-1.5"
                      onClick={() => { setNewImageFile(null); setNewImagePreview(null); if (fileRef.current) fileRef.current.value = ""; }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="w-full aspect-square border border-dashed border-white/20 hover:border-[#FF3B00] flex flex-col items-center justify-center gap-2 text-white/55 hover:text-white transition-colors"
                    data-testid="edit-replace-image-btn"
                  >
                    <Upload size={22} strokeWidth={1.2} />
                    <div className="label-mono">Upload new</div>
                  </button>
                )}
              </div>
            </div>
            {newImageFile && (
              <div className="mt-4 flex items-center gap-3 justify-between">
                <label className="flex items-center gap-2 text-sm text-white/75 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={regenerateVideo}
                    onChange={(e) => setRegenerateVideo(e.target.checked)}
                    className="accent-[#FF3B00]"
                  />
                  Re-render video with new artwork
                </label>
                <button
                  className="btn-amber-small"
                  onClick={uploadNewImage}
                  disabled={uploading}
                  data-testid="edit-replace-image-save"
                >
                  {uploading ? "Uploading…" : "Replace image"}
                </button>
              </div>
            )}
          </div>

          <div className="hr-faint" />

          <div>
            <div className="label-mono mb-2">Caption</div>
            <textarea
              className="mp-textarea"
              rows={6}
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              data-testid="edit-caption-textarea"
            />
          </div>
          <div>
            <div className="label-mono mb-2">Hashtags (space-separated)</div>
            <input
              className="mp-input"
              value={hashtags}
              onChange={(e) => setHashtags(e.target.value)}
              data-testid="edit-hashtags-input"
            />
          </div>
          <div>
            <div className="label-mono mb-2">Scheduled at</div>
            <input
              type="datetime-local"
              className="mp-input"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              data-testid="edit-scheduled-input"
            />
          </div>
        </div>
        <div className="flex gap-3 justify-end mt-6">
          <button className="btn-ghost-line" onClick={() => onOpenChange(false)} data-testid="edit-cancel-btn">
            Cancel
          </button>
          <button
            className="btn-amber"
            onClick={save}
            disabled={saving}
            data-testid="edit-save-btn"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
